import { useState } from "react";
import { ref, push } from "firebase/database";
import { database } from "../constants/firebase";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ICONS } from "../constants/icons";
import ConfirmationModal from "../components/ConfirmationModal";
import {
  LuSend,
  LuInfo,
  LuLink,
  LuMessageSquare,
  LuType,
} from "react-icons/lu";

const AdminBroadcast = () => {
  const { currentUser } = useUser();
  const navigate = useMoveTo();

  const [formData, setFormData] = useState({
    title: "",
    body: "",
    uri: "",
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  if (!currentUser?.isAdmin) {
    return (
      <div className="h-screen flex items-center justify-center bg-white dark:bg-[#1A1A1A]">
        <p className="text-gray-500">Access Denied</p>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendClick = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.body) {
      setStatus({ type: "error", message: "Title and Body are required." });
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmBroadcast = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      // Write broadcast to database
      // Cloud Function triggers automatically on write
      const broadcastsRef = ref(database, "broadcasts/pending");
      await push(broadcastsRef, {
        title: formData.title,
        body: formData.body,
        uri: formData.uri,
        adminUid: currentUser.uid,
        createdAt: new Date().toISOString(),
      });

      setStatus({
        type: "success",
        message: "📢 Broadcast sent successfully to all users!",
      });
      setFormData({ title: "", body: "", uri: "" });
    } catch (error) {
      console.error("Broadcast failed:", error);
      setStatus({
        type: "error",
        message: error.message || "Failed to send broadcast.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
      <MobileHeader title={"Broadcast"} isBackBtn={true} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-6">
          <div className="flex items-center space-x-4 mb-8">
            <button onClick={() => navigate(-1)} className="hidden md:block">
              <img
                src={ICONS.arrowBack}
                className="w-5 h-5 grayscale brightness-0 dark:invert"
                alt="Back"
              />
            </button>
            <h2 className="text-2xl md:text-3xl font-bold">Broadcast Portal</h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* --- Compose Section --- */}
              <div className="space-y-6">
                <div className="bg-white dark:bg-[#1f1f1f] p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
                  <h3 className="text-lg font-bold mb-6 flex items-center">
                    <LuMessageSquare className="mr-2 text-[#327691]" />
                    Compose Message
                  </h3>

                  <form onSubmit={handleSendClick} className="space-y-5">
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 block">
                        Notification Title
                      </label>
                      <div className="relative">
                        <LuType className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          placeholder="e.g. New Feature Alert! 🚀"
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-[#252525] border-none rounded-xl focus:ring-2 focus:ring-[#327691] outline-none transition-all"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 block">
                        Message Body
                      </label>
                      <textarea
                        name="body"
                        value={formData.body}
                        onChange={handleChange}
                        placeholder="Write your message here..."
                        rows="4"
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-[#252525] border-none rounded-xl focus:ring-2 focus:ring-[#327691] outline-none transition-all resize-none"
                        required
                      ></textarea>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 block">
                        Action Link (Optional)
                      </label>
                      <div className="relative">
                        <LuLink className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="uri"
                          value={formData.uri}
                          onChange={handleChange}
                          placeholder="https://zylen.app/#/..."
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-[#252525] border-none rounded-xl focus:ring-2 focus:ring-[#327691] outline-none transition-all"
                        />
                      </div>
                      <p className="text-[10px] text-gray-400 mt-2">
                        Redirects user when they tap the notification.
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-[#327691] hover:bg-[#2a637a] text-white rounded-xl font-bold shadow-lg shadow-[#327691]/20 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      ) : (
                        <>
                          <LuSend className="text-lg" />
                          <span>Broadcast to All Users</span>
                        </>
                      )}
                    </button>

                    {status.message && (
                      <div
                        className={`p-4 rounded-xl text-sm font-medium ${status.type === "success" ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400" : "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400"}`}
                      >
                        {status.message}
                      </div>
                    )}
                  </form>
                </div>
              </div>

              {/* --- Preview Section --- */}
              <div className="space-y-6">
                <div className="bg-white dark:bg-[#1f1f1f] p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col h-full">
                  <h3 className="text-lg font-bold mb-6 flex items-center">
                    <LuInfo className="mr-2 text-purple-500" />
                    Live Preview
                  </h3>

                  <div className="flex-1 flex flex-col items-center justify-center py-10 px-4">
                    {/* Mock Phone Notification */}
                    <div className="w-full max-w-[300px] bg-white/80 dark:bg-black/60 backdrop-blur-md rounded-2xl p-4 shadow-2xl border border-white/20">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-[#327691] rounded-lg flex items-center justify-center">
                          <img
                            src="/logo192.png"
                            alt="Zylen"
                            className="w-5 h-5 invert"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                            Zylen • Just now
                          </p>
                          <p className="text-sm font-bold truncate dark:text-white">
                            {formData.title || "Notification Title"}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-3">
                        {formData.body ||
                          "Your broadcast message will appear here for all users."}
                      </p>
                    </div>

                    <div className="mt-12 space-y-4 w-full">
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30">
                        <h4 className="text-sm font-bold text-blue-700 dark:text-blue-400 mb-1">
                          Target Audience
                        </h4>
                        <p className="text-xs text-blue-600/80 dark:text-blue-400/60">
                          Every user with push notifications enabled on Web or
                          Mobile.
                        </p>
                      </div>

                      <div className="p-4 bg-purple-50 dark:bg-purple-900/10 rounded-xl border border-purple-100 dark:border-purple-900/30">
                        <h4 className="text-sm font-bold text-purple-700 dark:text-purple-400 mb-1">
                          Method: FCM Topic
                        </h4>
                        <p className="text-xs text-purple-600/80 dark:text-purple-400/60">
                          Using scalable topic-based delivery for near-instant
                          distribution.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmBroadcast}
        title="Confirm Global Broadcast"
        confirmText="Send Broadcast"
        Icon={LuSend}
        message={`Are you sure you want to send this notification to ALL users? This action cannot be undone.`}
      />
    </div>
  );
};

export default AdminBroadcast;
